import React from 'react';
import { Check, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';

interface OptionCardProps {
  label: string;
  description?: string;
  icon?: LucideIcon;
  selected: boolean;
  onSelect: () => void;
}

/** A single-choice answer card in the staff onboarding (marks only — moving on stays on "Continue"). */
export function OptionCard({ label, description, icon: Icon, selected, onSelect }: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-3 rounded-md border p-4 text-left transition-colors',
        selected ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground/20 hover:bg-accent/50',
      )}
    >
      {Icon ? (
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            selected ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        {description ? <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span> : null}
      </span>
      <span
        aria-hidden
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-foreground bg-foreground text-background' : 'border-border',
        )}
      >
        {selected ? <Check className="h-3 w-3" /> : null}
      </span>
    </button>
  );
}

interface OptionChipProps {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

/** A pill toggle for "pick several" answers. */
export function OptionChip({ label, selected, disabled, onToggle }: OptionChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      disabled={disabled && !selected}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        selected
          ? 'border-foreground bg-foreground font-medium text-background'
          : 'border-border text-foreground/80 hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {selected ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
      {label}
    </button>
  );
}
